import { useRef, useState } from "react";
import { YEARS } from "@/types/video";

interface TimeDialProps {
  value: number | null;
  onChange: (year: number | null) => void;
  onClose: () => void;
  visible: boolean;
}

// Spec #8: bottom-of-screen year slider.
// Visual: vintage radio dial / time machine controller.

export default function TimeDial({ value, onChange, onClose, visible }: TimeDialProps) {
  const [dragValue, setDragValue] = useState<number | null>(null);
  const dragging = useRef(false);

  const min = YEARS[0];
  const max = YEARS[YEARS.length - 1];
  const displayValue = dragValue ?? value;
  const position = displayValue
    ? ((displayValue - min) / (max - min)) * 100
    : 50;

  const yearAtPointer = (element: HTMLDivElement, clientX: number) => {
    const rect = element.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Math.round(min + ratio * (max - min));
  };

  return (
    <div
      className={`pointer-events-auto transition-all duration-500 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      data-ui-zone
    >
      <div className="glass-panel px-6 py-4">
        <div className="flex justify-between items-center mb-3 font-mono text-[10px] uppercase tracking-widest2 text-static">
          <span>TIMELINE</span>
          <span className="vfd text-signal">
            {displayValue ?? "ALL"}
          </span>
          <div className="flex items-center gap-3">
            {value !== null && (
              <button
                onClick={() => onChange(null)}
                className="text-dim hover:text-signal transition-colors"
              >
                [RESET]
              </button>
            )}
            <button
              onClick={onClose}
              className="text-static hover:text-signal transition-colors"
              aria-label="关闭时间轴"
            >
              [CLOSE]
            </button>
          </div>
        </div>

        <div className="relative h-8">
          {/* Tick marks */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
            {YEARS.map((y) => (
              <div
                key={y}
                className="flex flex-col items-center"
                style={{ width: `${100 / (YEARS.length - 1)}%` }}
              >
                <div
                  className={`h-2 w-px ${
                    y === displayValue ? "bg-signal" : "bg-dim/60"
                  }`}
                />
              </div>
            ))}
          </div>

          {/* Track */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px time-dial-track" />

          {/* Draggable slider surface */}
          <div
            role="slider"
            tabIndex={0}
            aria-label="选择年份"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={displayValue ?? Math.round((min + max) / 2)}
            aria-valuetext={displayValue ? String(displayValue) : "全部年份"}
            onPointerDown={(event) => {
              dragging.current = true;
              event.currentTarget.setPointerCapture(event.pointerId);
              setDragValue(yearAtPointer(event.currentTarget, event.clientX));
            }}
            onPointerMove={(event) => {
              if (!dragging.current) return;
              setDragValue(yearAtPointer(event.currentTarget, event.clientX));
            }}
            onPointerUp={(event) => {
              if (!dragging.current) return;
              const nextValue = yearAtPointer(event.currentTarget, event.clientX);
              dragging.current = false;
              event.currentTarget.releasePointerCapture(event.pointerId);
              setDragValue(null);
              if (nextValue !== value) onChange(nextValue);
            }}
            onPointerCancel={() => {
              dragging.current = false;
              setDragValue(null);
            }}
            onKeyDown={(event) => {
              const current = displayValue ?? Math.round((min + max) / 2);
              let nextValue: number | null = null;
              if (event.key === "ArrowLeft" || event.key === "ArrowDown") nextValue = Math.max(min, current - 1);
              if (event.key === "ArrowRight" || event.key === "ArrowUp") nextValue = Math.min(max, current + 1);
              if (event.key === "Home") nextValue = min;
              if (event.key === "End") nextValue = max;
              if (nextValue === null) return;
              event.preventDefault();
              onChange(nextValue);
            }}
            className="absolute inset-0 z-10 w-full cursor-grab touch-none active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/70"
          />

          {/* Dial knob */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 pointer-events-none"
            style={{ left: `${position}%` }}
          >
            <div className="relative">
              <div className="w-5 h-5 rounded-full bg-signal shadow-[0_0_12px_rgba(255,87,34,0.7)]" />
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 font-mono text-[10px] vfd">
                {displayValue ?? "ALL"}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-2 font-mono text-[9px] tracking-widest2 text-dim">
          <span>{min}</span>
          <span>{max}</span>
        </div>
      </div>
    </div>
  );
}
